import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

type MainTab = 'idea' | 'feedback';
type IdeaSection = 'overview' | 'howItWorks' | 'governance' | 'risks' | 'scope';

interface IdeaModalProps {
  forceOpen?: boolean;
  onClose?: () => void;
}

const IdeaModal = ({ forceOpen, onClose }: IdeaModalProps) => {
  const [visible, setVisible] = useState(false);
  const [mainTab, setMainTab] = useState<MainTab>('idea');
  const [ideaSection, setIdeaSection] = useState<IdeaSection>('overview');
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const navigate = useNavigate();

  // Auto-show on mount
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(timer);
  }, []);

  // Open when forceOpen triggered externally
  useEffect(() => {
    if (forceOpen) {
      setVisible(true);
      setMainTab('idea');
      setIdeaSection('overview');
    }
  }, [forceOpen]);

  // Lock body scroll when modal is visible
  useEffect(() => {
    if (visible) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflowY = 'scroll';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
    };
  }, [visible]);

  const handleClose = () => {
    setVisible(false);
    onClose?.();
  };

  const handleBrowse = () => {
    handleClose();
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= 500) {
      setFeedbackText(e.target.value);
      setCharCount(e.target.value.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!feedbackType || !feedbackText.trim()) return;
    setSubmitting(true);
    const formData = new URLSearchParams();
    formData.append('feedback_type', feedbackType);
    formData.append('feedback', feedbackText);
    if (email) formData.append('email', email);
    formData.append('timestamp', new Date().toISOString());
    try {
      await fetch('https://readdy.ai/api/form/d7f0642a509fjl5l010g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  const ideaSections: { key: IdeaSection; label: string; icon: string }[] = [
    { key: 'overview', label: 'نظرة عامة', icon: 'ri-lightbulb-line' },
    { key: 'howItWorks', label: 'آلية العمل', icon: 'ri-route-line' },
    { key: 'governance', label: 'الحوكمة', icon: 'ri-shield-star-line' },
    { key: 'risks', label: 'إدارة المخاطر', icon: 'ri-error-warning-line' },
    { key: 'scope', label: 'نطاق الاستخدام', icon: 'ri-focus-3-line' },
  ];

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center p-3 md:p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }}
      dir="rtl"
    >
      <div
        className="relative bg-white rounded-2xl w-full max-w-2xl flex flex-col"
        style={{ fontFamily: "'Noto Naskh Arabic', serif", maxHeight: '90vh' }}
      >
        {/* ── Header ── */}
        <div className="px-5 pt-5 pb-4 border-b border-stone-100 shrink-0">
          <button
            onClick={handleClose}
            className="absolute top-4 left-4 w-8 h-8 flex items-center justify-center rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
              نسخة تجريبية — Pilot
            </span>
            <span className="text-xs text-stone-400">جمعية ديوان الشبابية</span>
          </div>

          <h2 className="text-lg md:text-xl font-bold text-stone-800 leading-snug">
            منصة هِبة — دعم مجتمعي لتمكين الشباب
          </h2>
          <p className="text-xs text-stone-500 mt-1 leading-relaxed">
            مبادرة رقمية تربط بين احتياجات الشباب ودعم المجتمع عبر تمويلات موثّقة وشفافة.
          </p>

          {/* Main tabs */}
          <div className="flex gap-1 mt-3 bg-stone-100 rounded-full p-1 w-fit">
            <button
              onClick={() => setMainTab('idea')}
              className={`text-xs font-medium px-4 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                mainTab === 'idea' ? 'bg-white text-emerald-700 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              عرض الفكرة
            </button>
            <button
              onClick={() => setMainTab('feedback')}
              className={`text-xs font-medium px-4 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                mainTab === 'feedback' ? 'bg-white text-emerald-700 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              أرسل ملاحظتك
            </button>
          </div>
        </div>

        {/* ── Idea Tab ── */}
        {mainTab === 'idea' && (
          <>
            {/* Sub-nav */}
            <div className="flex flex-wrap gap-1.5 px-4 py-3 border-b border-stone-100 shrink-0">
              {ideaSections.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setIdeaSection(s.key)}
                  className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer transition-all ${
                    ideaSection === s.key
                      ? 'bg-emerald-600 text-white'
                      : 'bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-700'
                  }`}
                >
                  <i className={`${s.icon} text-xs`}></i>
                  {s.label}
                </button>
              ))}
            </div>

            {/* Section content */}
            <div className="overflow-y-auto flex-1 px-5 py-4">

              {/* ── Overview ── */}
              {ideaSection === 'overview' && (
                <div className="space-y-4">
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <p className="text-sm font-semibold text-emerald-800 mb-1.5">ما هي منصة هِبة؟</p>
                    <p className="text-xs text-emerald-700 leading-relaxed">
                      مبادرة رقمية أطلقتها جمعية ديوان الشبابية لتمكين الشباب من تحويل أفكارهم واحتياجاتهم إلى فرص ممولة من المجتمع، ضمن إطار موثّق وشفاف.
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-stone-600 mb-2 flex items-center gap-1.5">
                      <i className="ri-error-warning-line text-stone-400"></i>
                      المشكلة التي تعالجها
                    </p>
                    <div className="space-y-2">
                      {[
                        { icon: 'ri-close-circle-line', color: 'text-red-400', text: 'صعوبة وصول الشباب لتمويل أولي سريع ومرن' },
                        { icon: 'ri-close-circle-line', color: 'text-red-400', text: 'تشتت الجهود المجتمعية في دعم مبادرات غير موثّقة' },
                        { icon: 'ri-close-circle-line', color: 'text-red-400', text: 'غياب قنوات موثوقة تجمع الاحتياج الحقيقي بالرغبة في العطاء' },
                      ].map((item) => (
                        <div key={item.text} className="flex items-start gap-2.5 bg-stone-50 rounded-lg p-2.5">
                          <i className={`${item.icon} ${item.color} text-sm mt-0.5 shrink-0`}></i>
                          <p className="text-xs text-stone-600 leading-relaxed">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-stone-50 rounded-xl p-3 text-center">
                      <p className="text-xl font-bold text-emerald-600">5,000</p>
                      <p className="text-xs text-stone-500 mt-0.5">ريال سقف أولي للطلب</p>
                    </div>
                    <div className="bg-stone-50 rounded-xl p-3 text-center">
                      <p className="text-xl font-bold text-emerald-600">5%</p>
                      <p className="text-xs text-stone-500 mt-0.5">نسبة تشغيلية للجمعية</p>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2">
                    <i className="ri-information-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
                    <p className="text-xs text-amber-700 leading-relaxed">
                      هذه نسخة تجريبية (Pilot)، الهدف منها اختبار الفكرة وتحسينها قبل الإطلاق الرسمي.
                    </p>
                  </div>
                </div>
              )}

              {/* ── How It Works ── */}
              {ideaSection === 'howItWorks' && (
                <div className="space-y-4">
                  {[
                    {
                      letter: 'أ',
                      title: 'نشر الطلب',
                      icon: 'ri-user-add-line',
                      color: 'bg-emerald-100 text-emerald-600',
                      items: [
                        'تسجيل عبر نفاذ للتحقق من الهوية',
                        'وصف المشروع أو الاحتياج بوضوح',
                        'تحديد المبلغ المطلوب (≤ 5,000 ريال)',
                        'خطة استخدام المبلغ ومؤشرات النجاح',
                        'مستندات داعمة (عروض أسعار، قبول دورة...)',
                      ],
                    },
                    {
                      letter: 'ب',
                      title: 'المراجعة والتوثيق',
                      icon: 'ri-shield-check-line',
                      color: 'bg-sky-100 text-sky-600',
                      items: [
                        'فريق الجمعية يراجع وفق معايير محددة',
                        'تقييم الجدوى والمصداقية والأثر',
                        'تصنيف الطلب (مشروع / تدريب / احتياج ملح)',
                        'اعتماد الطلب مع درجة توثيق واضحة',
                      ],
                    },
                    {
                      letter: 'ج',
                      title: 'التمويل المجتمعي',
                      icon: 'ri-heart-line',
                      color: 'bg-rose-100 text-rose-500',
                      items: [
                        'الأفراد يدعمون بمبالغ مرنة (100 – 3,000 ريال)',
                        'عرض تقدم التمويل لحظياً',
                        'دعم سريع (One-click) + محفظة داخلية',
                      ],
                    },
                    {
                      letter: 'د',
                      title: 'الصرف والمتابعة',
                      icon: 'ri-bank-line',
                      color: 'bg-amber-100 text-amber-600',
                      items: [
                        'صرف دفعة واحدة أو على مراحل',
                        'التحقق من الاستخدام (فواتير / عقود / إثباتات)',
                        'نشر تحديثات مرحلية للداعمين',
                      ],
                    },
                    {
                      letter: 'هـ',
                      title: 'الإغلاق والتقارير',
                      icon: 'ri-bar-chart-line',
                      color: 'bg-violet-100 text-violet-600',
                      items: [
                        'تقرير ختامي عند اكتمال التنفيذ',
                        'نتائج ومخرجات وأثر قابل للقياس',
                        'أرشفة الحالة مع مؤشرات أداء',
                      ],
                    },
                  ].map((phase) => (
                    <div key={phase.letter} className="border border-stone-100 rounded-xl overflow-hidden">
                      <div className="flex items-center gap-3 px-4 py-3 bg-stone-50">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-full ${phase.color} shrink-0`}>
                          <i className={`${phase.icon} text-sm`}></i>
                        </div>
                        <div>
                          <span className="text-xs text-stone-400 font-medium">المرحلة {phase.letter}</span>
                          <p className="text-sm font-semibold text-stone-700 leading-none mt-0.5">{phase.title}</p>
                        </div>
                      </div>
                      <ul className="px-4 py-3 space-y-1.5">
                        {phase.items.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-xs text-stone-600">
                            <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5 shrink-0"></i>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                  <div className="bg-stone-50 rounded-xl p-3 flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-stone-200 text-stone-600 shrink-0">
                      <i className="ri-git-branch-line text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-stone-700">المشاريع الكبيرة — نظام المراحل</p>
                      <p className="text-xs text-stone-500 mt-0.5 leading-relaxed">
                        تُجزّأ إلى Milestones: مرحلة 1 ← تمويل ← تحقق ← تقرير ← مرحلة 2... مما يقلل المخاطر ويرفع معدل النجاح.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Governance ── */}
              {ideaSection === 'governance' && (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-stone-600 mb-2.5 flex items-center gap-1.5">
                      <i className="ri-shield-star-line text-emerald-500"></i>
                      الحوكمة والامتثال
                    </p>
                    <div className="space-y-2">
                      {[
                        { icon: 'ri-fingerprint-line', title: 'تحقق الهوية عبر نفاذ', desc: 'ربط كل حساب بهوية موثّقة رسمياً' },
                        { icon: 'ri-bank-card-line', title: 'ربط وسائل الدفع', desc: 'حسابات بنكية موثّقة فقط' },
                        { icon: 'ri-file-list-3-line', title: 'سجلات تدقيق (Audit Logs)', desc: 'توثيق كامل لكل عملية مالية' },
                        { icon: 'ri-user-search-line', title: 'سياسات KYC / AML', desc: 'متدرّجة حسب المبالغ والتكرار' },
                        { icon: 'ri-file-shield-2-line', title: 'شروط استخدام واضحة', desc: 'مع آلية شكاوى واسترداد' },
                      ].map((item) => (
                        <div key={item.title} className="flex items-start gap-3 p-3 border border-stone-100 rounded-xl">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 shrink-0">
                            <i className={`${item.icon} text-sm`}></i>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-stone-700">{item.title}</p>
                            <p className="text-xs text-stone-400 mt-0.5">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-stone-600 mb-2.5 flex items-center gap-1.5">
                      <i className="ri-eye-line text-emerald-500"></i>
                      نموذج الثقة والشفافية
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { icon: 'ri-medal-line', text: 'درجة توثيق لكل طلب (أساسي / متقدم / معزز)' },
                        { icon: 'ri-file-search-line', text: 'عرض الأدلة الداعمة مختصرة' },
                        { icon: 'ri-arrow-left-right-line', text: 'تتبع الأثر قبل وبعد التنفيذ' },
                        { icon: 'ri-bar-chart-grouped-line', text: 'تقارير دورية منشورة للعموم' },
                      ].map((item) => (
                        <div key={item.text} className="bg-stone-50 rounded-xl p-3 flex items-start gap-2">
                          <i className={`${item.icon} text-emerald-500 text-sm mt-0.5 shrink-0`}></i>
                          <p className="text-xs text-stone-600 leading-relaxed">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-stone-50 rounded-xl p-3 flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-stone-200 text-stone-600 shrink-0">
                      <i className="ri-percent-line text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-stone-700">نموذج التشغيل</p>
                      <p className="text-xs text-stone-500 mt-0.5 leading-relaxed">
                        الجمعية تأخذ <strong>5%</strong> من كل مساهمة لتغطية التحقق، المتابعة، الامتثال، والدعم التقني.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Risks ── */}
              {ideaSection === 'risks' && (
                <div className="space-y-3">
                  {[
                    {
                      label: 'أ',
                      title: 'مخاطر إساءة الاستخدام المالي',
                      subtitle: 'غسل الأموال / التحايل',
                      iconColor: 'bg-red-100 text-red-500',
                      indicators: [
                        'طلبات متكررة بمبالغ قريبة من الحد الأعلى',
                        'حسابات جديدة تدعم بعضها بشكل دائري',
                        'تغيّر مفاجئ في نمط التبرعات',
                      ],
                      controls: [
                        'حدود يومية/شهرية للمساهمات',
                        'تحليل شبكي لاكتشاف العلاقات بين الحسابات',
                        'تجميد مؤقت عند الاشتباه + مراجعة يدوية',
                        'اشتراط فواتير من جهات معروفة',
                        'تتبع الأجهزة (Device Fingerprinting)',
                      ],
                    },
                    {
                      label: 'ب',
                      title: 'مخاطر فشل المشاريع',
                      subtitle: 'ضعف التخطيط / نقص الخبرة',
                      iconColor: 'bg-amber-100 text-amber-600',
                      indicators: [
                        'ضعف الخطة أو غموض الاستخدام',
                        'نقص الخبرة لدى مقدم الطلب',
                      ],
                      controls: [
                        'تقييم جدوى مبسّط قبل النشر',
                        'تقسيم التمويل إلى مراحل',
                        'صرف جزئي مشروط بتحقيق KPIs',
                        'إتاحة مرشدين متطوعين لدعم التنفيذ',
                      ],
                    },
                    {
                      label: 'ج',
                      title: 'مخاطر السمعة والثقة',
                      subtitle: 'تأخر التحديثات / شكاوى',
                      iconColor: 'bg-orange-100 text-orange-500',
                      indicators: [
                        'تأخر التحديثات من المستفيد',
                        'شكاوى متكررة من الداعمين',
                      ],
                      controls: [
                        'لوحة شفافية عامة (نسب النجاح والإخفاق)',
                        'إلزام تحديثات دورية للمستفيد',
                        'نظام تقييم علني للحالات المكتملة',
                      ],
                    },
                    {
                      label: 'د',
                      title: 'مخاطر تقنية',
                      subtitle: 'اختراق / تزوير',
                      iconColor: 'bg-stone-200 text-stone-600',
                      indicators: [
                        'محاولات اختراق أو تزوير البيانات',
                      ],
                      controls: [
                        'مصادقة متعددة العوامل (MFA)',
                        'تشفير البيانات الحساسة',
                        'اختبارات اختراق دورية',
                        'مراقبة آنية (SIEM) وتنبيهات',
                      ],
                    },
                  ].map((risk) => (
                    <details key={risk.label} className="border border-stone-100 rounded-xl overflow-hidden group">
                      <summary className="flex items-center gap-3 px-4 py-3 bg-stone-50 cursor-pointer list-none">
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full ${risk.iconColor} shrink-0 text-xs font-bold`}>
                          {risk.label}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-stone-700">{risk.title}</p>
                          <p className="text-xs text-stone-400">{risk.subtitle}</p>
                        </div>
                        <i className="ri-arrow-down-s-line text-stone-400 transition-transform group-open:rotate-180"></i>
                      </summary>
                      <div className="px-4 py-3 space-y-3">
                        <div>
                          <p className="text-xs font-semibold text-stone-500 mb-1.5">مؤشرات الخطر</p>
                          <ul className="space-y-1">
                            {risk.indicators.map((ind) => (
                              <li key={ind} className="flex items-start gap-2 text-xs text-stone-600">
                                <i className="ri-alert-line text-amber-400 mt-0.5 shrink-0"></i>
                                {ind}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-stone-500 mb-1.5">الضوابط المقترحة</p>
                          <ul className="space-y-1">
                            {risk.controls.map((ctrl) => (
                              <li key={ctrl} className="flex items-start gap-2 text-xs text-stone-600">
                                <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5 shrink-0"></i>
                                {ctrl}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              )}

              {/* ── Scope ── */}
              {ideaSection === 'scope' && (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-stone-600 mb-2.5 flex items-center gap-1.5">
                      <i className="ri-focus-3-line text-emerald-500"></i>
                      نطاق الاستخدام المدعوم
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { icon: 'ri-rocket-line', title: 'مشاريع شبابية', desc: 'أفكار ومشاريع صغيرة قابلة للتنفيذ' },
                        { icon: 'ri-graduation-cap-line', title: 'دورات وشهادات', desc: 'تدريب مهني وشهادات معتمدة' },
                        { icon: 'ri-tools-line', title: 'أدوات العمل', desc: 'عدة، أجهزة، معدات أساسية' },
                        { icon: 'ri-heart-pulse-line', title: 'احتياجات ملحّة', desc: 'مرتبطة بالتمكين الشخصي' },
                      ].map((item) => (
                        <div key={item.title} className="border border-stone-100 rounded-xl p-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mb-2">
                            <i className={`${item.icon} text-sm`}></i>
                          </div>
                          <p className="text-xs font-semibold text-stone-700">{item.title}</p>
                          <p className="text-xs text-stone-400 mt-0.5 leading-relaxed">{item.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-stone-600 mb-2.5 flex items-center gap-1.5">
                      <i className="ri-search-eye-line text-emerald-500"></i>
                      ما نبحث عنه في هذه المرحلة
                    </p>
                    <div className="space-y-2">
                      {[
                        { icon: 'ri-shield-keyhole-line', text: 'ملاحظات على المخاطر (أمنية / تقنية / تشغيلية)' },
                        { icon: 'ri-verified-badge-line', text: 'اقتراحات لتحسين التحقق والامتثال' },
                        { icon: 'ri-expand-diagonal-line', text: 'تقييم وضوح النموذج وقابلية التوسع' },
                        { icon: 'ri-bug-line', text: 'أي اعتراضات أو سيناريوهات إساءة استخدام محتملة' },
                      ].map((item) => (
                        <div key={item.text} className="flex items-start gap-2.5 bg-stone-50 rounded-lg p-2.5">
                          <i className={`${item.icon} text-emerald-500 text-sm mt-0.5 shrink-0`}></i>
                          <p className="text-xs text-stone-600 leading-relaxed">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-emerald-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-emerald-800 mb-1">خلاصة تشغيلية</p>
                    <p className="text-xs text-emerald-700 leading-relaxed">
                      "هِبة" تربط بين احتياج موثّق ودعم مجتمعي مباشر ضمن إطار حوكمة واضح، مع تقليل المخاطر عبر الحدود، التجزئة المرحلية، والتحقق متعدد الطبقات. الهدف رفع كفاءة العطاء وتحويله إلى أثر قابل للقياس.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-stone-100 px-5 py-3.5 flex flex-col sm:flex-row gap-2.5 shrink-0">
              <button
                onClick={handleBrowse}
                className="flex-1 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                تصفح النسخة التجريبية
              </button>
              <button
                onClick={() => setMainTab('feedback')}
                className="flex-1 py-2.5 rounded-full border border-stone-200 text-stone-700 text-sm font-medium hover:border-emerald-300 hover:text-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                إرسال ملاحظة
              </button>
            </div>
          </>
        )}

        {/* ── Feedback Tab ── */}
        {mainTab === 'feedback' && (
          <div className="overflow-y-auto flex-1 px-5 py-4">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mx-auto mb-4">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="text-base font-bold text-stone-800 mb-2">شكراً على ملاحظتك!</h3>
                <p className="text-xs text-stone-500">رأيك يساعدنا على تحسين المنصة قبل الإطلاق.</p>
                <button
                  onClick={() => { setSubmitted(false); setFeedbackText(''); setFeedbackType(''); setEmail(''); setCharCount(0); }}
                  className="mt-5 text-sm text-emerald-600 hover:underline cursor-pointer"
                >
                  إرسال ملاحظة أخرى
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-stone-800 mb-1">نحتاج رأيك قبل الإطلاق</p>
                  <p className="text-xs text-stone-400 leading-relaxed">
                    ملاحظاتك تساعدنا على اكتشاف المخاطر وتحسين التجربة — سواء كانت أمنية، تقنية، أو تشغيلية.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-700 mb-1.5">
                    نوع الملاحظة <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="feedback_type"
                      value={feedbackType}
                      onChange={(e) => setFeedbackType(e.target.value)}
                      required
                      className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2.5 bg-white text-stone-700 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
                    >
                      <option value="">اختر نوع الملاحظة</option>
                      <option value="أمني">أمني</option>
                      <option value="تقني">تقني</option>
                      <option value="تجربة المستخدم">تجربة المستخدم</option>
                      <option value="تشغيلي">تشغيلي</option>
                      <option value="امتثال وحوكمة">امتثال وحوكمة</option>
                      <option value="نموذج تشغيلي">نموذج تشغيلي</option>
                      <option value="أخرى">أخرى</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                      <i className="ri-arrow-down-s-line text-stone-400"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-700 mb-1.5">
                    نص الملاحظة <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="feedback"
                    value={feedbackText}
                    onChange={handleTextareaChange}
                    required
                    rows={4}
                    placeholder="اكتب ملاحظتك هنا..."
                    className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2.5 text-stone-700 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
                  />
                  <div className="flex justify-between mt-1">
                    <span className={`text-xs ${charCount >= 500 ? 'text-red-400' : 'text-stone-400'}`}>{charCount}/500</span>
                    {charCount >= 500 && <span className="text-xs text-red-400">وصلت للحد الأقصى</span>}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-700 mb-1.5">
                    البريد الإلكتروني <span className="text-stone-400 font-normal">(اختياري)</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="للتواصل معك إن احتجنا توضيحاً"
                    className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2.5 text-stone-700 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || !feedbackType || !feedbackText.trim() || charCount > 500}
                  className="w-full py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {submitting ? 'جاري الإرسال...' : 'إرسال الملاحظة'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IdeaModal;
